import {
  EvidenceTypeEnum,
  PaginatedEvidenceList,
  useApiEvidenceV1SiteMaterialSamplesEvidenceList,
  useApiSamplesV1SamplesEvidenceList,
  useApiTradeV1SiteMaterialsEvidenceList,
} from 'api';
import { useEffect, useMemo, useState } from 'react';

import { EvidenceInformation, EvidencePhoto } from '../../types';

const dataTransformation = (data?: PaginatedEvidenceList): EvidencePhoto[] => {
  if (!data) {
    return [];
  }

  return data.results.map((evidence) => {
    return {
      annotations: evidence.model_results?.coco_prediction || [],
      id: evidence.id,
      preview: evidence.preview ? String(evidence.preview) : String(evidence.file),
      url: String(evidence.file),
    };
  });
};

const updateEvidenceCategory = (
  currentEvidence: EvidenceInformation,
  category: keyof EvidenceInformation,
  newData: EvidencePhoto[] | undefined
): EvidenceInformation => {
  if (!newData) return currentEvidence;

  const existingIds = new Set(currentEvidence[category].map((item) => item.id));
  const uniqueNewItems = newData.filter((item) => !existingIds.has(item.id));

  return {
    ...currentEvidence,
    [category]: [...currentEvidence[category], ...uniqueNewItems],
  };
};

export const useQualityReportMedia = (
  siteMaterialId: string,
  {
    pageSize,
    fetchOnly,
    manualSampleId,
  }: { pageSize: number; fetchOnly?: keyof EvidenceInformation; manualSampleId?: number }
): { balesAccepted: number; isLoading: boolean; evidence: EvidenceInformation; hasMore: boolean } => {
  const [evidence, setEvidence] = useState<EvidenceInformation>({
    accepted: [],
    ai_loose: [],
    loading: [],
    marketing: [],
    sample: [],
    site: [],
    specification: [],
  });

  const [loadedPageSizes, setLoadedPageSizes] = useState<Set<number>>(new Set());
  const [currentSampleId, setCurrentSampleId] = useState<number | undefined>(manualSampleId);

  useEffect(() => {
    if (manualSampleId !== currentSampleId) {
      // eslint-disable-next-line react-hooks/set-state-in-effect
      setCurrentSampleId(manualSampleId);

      if (currentSampleId !== undefined || manualSampleId !== undefined) {
        // eslint-disable-next-line react-hooks/set-state-in-effect
        setEvidence((prev) => ({
          ...prev,
          sample: [],
        }));
        // eslint-disable-next-line react-hooks/set-state-in-effect
        setLoadedPageSizes(new Set());
      }
    }
  }, [manualSampleId, currentSampleId]);

  const enableSampleFetching = Boolean(manualSampleId) && (fetchOnly ? fetchOnly === 'sample' : true);

  const { data: sampleEvidence, isLoading: sampleEvidenceIsLoading } = useApiSamplesV1SamplesEvidenceList(
    Number(manualSampleId),
    {
      order: '-created_at',
      page_size: pageSize,
      type: EvidenceTypeEnum.SB,
    },
    {
      query: {
        enabled: enableSampleFetching,
        select: dataTransformation,
      },
    }
  );

  const { data: accepted, isLoading: acceptedIsLoading } = useApiTradeV1SiteMaterialsEvidenceList(
    siteMaterialId,
    {
      include_self_serve_sessions: true,
      only_accepted_sop: true,
      order: '-created_at',
      page_size: pageSize,
      type: EvidenceTypeEnum.BP,
    },
    { query: { enabled: fetchOnly ? fetchOnly === 'accepted' : true } }
  );

  const acceptedEvidence = useMemo(() => dataTransformation(accepted), [accepted]);

  const { data: ai_loose, isLoading: aiGraviIsLoading } = useApiEvidenceV1SiteMaterialSamplesEvidenceList(
    siteMaterialId,
    {
      include_self_serve_samples: true,
      order: '-created_at',
      page_size: pageSize,
      type: EvidenceTypeEnum.AS,
    },
    { query: { enabled: fetchOnly ? fetchOnly === 'ai_loose' : true, select: dataTransformation } }
  );

  const { data: loading, isLoading: loadingIsLoading } = useApiTradeV1SiteMaterialsEvidenceList(
    siteMaterialId,
    { order: '-created_at', page_size: pageSize, type: EvidenceTypeEnum.LP },
    { query: { enabled: fetchOnly ? fetchOnly === 'loading' : true, select: dataTransformation } }
  );
  const { data: marketing, isLoading: marketingIsLoading } = useApiTradeV1SiteMaterialsEvidenceList(
    siteMaterialId,
    { order: '-created_at', page_size: pageSize, type: EvidenceTypeEnum.MP },
    { query: { enabled: fetchOnly ? fetchOnly === 'marketing' : true, select: dataTransformation } }
  );

  const { data: site, isLoading: siteIsLoading } = useApiTradeV1SiteMaterialsEvidenceList(
    siteMaterialId,
    { order: '-created_at', page_size: pageSize, type: EvidenceTypeEnum.SP },
    { query: { enabled: fetchOnly ? fetchOnly === 'site' : true, select: dataTransformation } }
  );
  const { data: specification, isLoading: specificationIsLoading } = useApiTradeV1SiteMaterialsEvidenceList(
    siteMaterialId,
    { order: '-created_at', page_size: pageSize, type: EvidenceTypeEnum.MS },
    { query: { enabled: fetchOnly ? fetchOnly === 'specification' : true, select: dataTransformation } }
  );

  const isLoading =
    specificationIsLoading ||
    siteIsLoading ||
    marketingIsLoading ||
    loadingIsLoading ||
    acceptedIsLoading ||
    aiGraviIsLoading ||
    sampleEvidenceIsLoading;

  useEffect(() => {
    if (loadedPageSizes.has(pageSize)) return;

    const dataIsReady = fetchOnly
      ? (fetchOnly === 'accepted' && acceptedEvidence) ||
        (fetchOnly === 'ai_loose' && ai_loose) ||
        (fetchOnly === 'loading' && loading) ||
        (fetchOnly === 'marketing' && marketing) ||
        (fetchOnly === 'sample' && sampleEvidence) ||
        (fetchOnly === 'site' && site) ||
        (fetchOnly === 'specification' && specification)
      : Boolean(acceptedEvidence || ai_loose || loading || marketing || sampleEvidence || site || specification);

    if (!dataIsReady || isLoading) return;

    let updatedEvidence = { ...evidence };

    const categories: Array<[keyof EvidenceInformation, EvidencePhoto[] | undefined]> = [
      ['accepted', acceptedEvidence],
      ['ai_loose', ai_loose],
      ['loading', loading],
      ['marketing', marketing],
      ['sample', sampleEvidence],
      ['site', site],
      ['specification', specification],
    ];

    categories.forEach(([category, data]) => {
      if (!fetchOnly || fetchOnly === category) {
        updatedEvidence = updateEvidenceCategory(updatedEvidence, category, data);
      }
    });

    // eslint-disable-next-line react-hooks/set-state-in-effect
    setEvidence(updatedEvidence);
    // eslint-disable-next-line react-hooks/set-state-in-effect
    setLoadedPageSizes((prev) => new Set([...prev, pageSize]));
  }, [
    pageSize,
    fetchOnly,
    acceptedEvidence,
    ai_loose,
    loading,
    marketing,
    sampleEvidence,
    site,
    specification,
    evidence,
    loadedPageSizes,
    isLoading,
  ]);

  const hasMore =
    acceptedEvidence?.length === pageSize ||
    ai_loose?.length === pageSize ||
    loading?.length === pageSize ||
    marketing?.length === pageSize ||
    sampleEvidence?.length === pageSize ||
    site?.length === pageSize ||
    specification?.length === pageSize;

  return {
    balesAccepted: accepted?.count || 0,
    evidence,
    hasMore,
    isLoading,
  };
};
